import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, CircularProgress, FormControlLabel, IconButton, Switch, Typography } from "@mui/material";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import GpsOffIcon from "@mui/icons-material/GpsOff";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import EntityList from "src/components/hallinta/EntityList";
import EntityFormDialog from "src/components/hallinta/EntityFormDialog";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import * as api from "src/utils/hallinta/hallintaApi";
import { isVehicleActive } from "src/utils/hallinta/vehicleActive";
import { captureError } from "src/utils/errorReporting";

// Search field config for the shared EntityList controls (module constant so
// its array identity is stable across renders — key names only, no copy).
const VEHICLE_SEARCH_KEYS = ["vehicleNimi", "vehicleRegNo", "vehicleNo", "vehicleTypeName"];

// Sort-option labels are language-bearing, so this is a function of `t`
// (mirrors quoteConstants.js's *Options(t) pattern) rather than a module-level
// constant — a plain constant would freeze the labels to Finnish at import.
function vehicleSortOptions(t) {
  return [
    { key: "vehicleNimi", label: t("provider.vehicles.nameLabel") },
    { key: "vehicleRegNo", label: t("provider.vehicles.regNoLabel") },
    { key: "vehiclePuomi", label: t("provider.vehicles.boomSortLabel") },
    { key: "vehicleNo", label: t("provider.vehicles.vehicleNoLabel") },
  ];
}

// Local-date YYYY-MM-DD for the vehicle_save @lastDate DATE param (Finland tz).
function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function VehiclesTab({ company, canEdit }) {
  const { token } = useAuth();
  const t = useT();
  const tenant = company.ownerAsiakasId ?? company.asiakasId;
  // ecofleet module flag comes from auth payload asiakasList[n].enabledModules.ecofleet
  // (asiakasSettings typeId 15). Gates the GPS switch in the editor and the GPS icon in the list.
  const hasGps = !!company.enabledModules?.ecofleet;
  const [rows, setRows] = useState(null);
  const [types, setTypes] = useState([]);
  const [error, setError] = useState(false);
  const [editRow, setEditRow] = useState(undefined);
  const [showInactive, setShowInactive] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const [list, t] = await Promise.all([
        api.listVehicles({ token, ownerAsiakasId: tenant }),
        api.listVehicleTypes({ token, ownerAsiakasId: tenant }),
      ]);
      // vehicle_list returns every non-deleted vehicle (active + retired); decorate
      // each with `active` (firstDate–lastDate window, same as puminet4's
      // vehicle_fetchList) and filter to active unless the user opts to show retired.
      setRows((list || []).map((v) => ({ ...v, __key: v.vehicleId, active: isVehicleActive(v) })));
      setTypes(t || []);
    } catch (err) {
      captureError(err, { tags: { entity: "vehicle", operation: "list" } });
      setError(true); setRows([]);
    }
  }, [token, tenant]);

  useEffect(() => { (async () => { await load(); })(); }, [load]);

  const visibleRows = useMemo(() => {
    if (rows == null) return null;
    return showInactive ? rows : rows.filter((v) => v.active);
  }, [rows, showInactive]);

  const isEdit = !!(editRow && editRow.vehicleId);

  const schema = useMemo(() => [
    { key: "vehicleNimi", label: t("provider.vehicles.nameLabel"), type: "text", required: true },
    { key: "vehicleRegNo", label: t("provider.vehicles.regNoLabel"), type: "text" },
    { key: "vehicleNo", label: t("provider.vehicles.vehicleNoLabel"), type: "text" },
    { key: "vehiclePuomi", label: t("provider.vehicles.boomFieldLabel"), type: "text" },
    {
      key: "vehicleTypeId",
      label: t("provider.vehicles.typeLabel"),
      type: "select",
      options: types.map((vt) => ({ value: vt.vehicleTypeId, label: vt.vehicleTypeName })),
    },
    { key: "memo", label: t("provider.vehicles.memoLabel"), type: "text" },
    ...(isEdit && hasGps ? [{ key: "hasGpsTracking", label: t("provider.vehicles.gpsTrackingLabel"), type: "switch" }] : []),
  ], [types, isEdit, hasGps, t]);

  async function submit(form) {
    const fields = {
      vehicleNimi: form.vehicleNimi,
      vehicleRegNo: form.vehicleRegNo || null,
      vehicleNo: form.vehicleNo ? Number(form.vehicleNo) : null,
      vehiclePuomi: form.vehiclePuomi ? Number(String(form.vehiclePuomi).replace(",", ".")) : null,
      vehicleTypeId: form.vehicleTypeId ? Number(form.vehicleTypeId) : null,
      memo: form.memo || null,
    };
    if (isEdit && hasGps) fields.hasGpsTracking = !!form.hasGpsTracking;
    if (editRow) {
      const { __key: _k, active: _a, ...base } = editRow;
      await api.saveVehicle({ token, fields: { ...base, ...fields, vehicleId: editRow.vehicleId, asiakasId: tenant } });
    } else await api.createVehicle({ token, ownerAsiakasId: tenant, fields, t });
    await load();
  }

  async function reactivate(v) {
    setReactivatingId(v.vehicleId);
    setActionError(null);
    try {
      await api.setVehicleLastDate({ token, vehicle: v });
      await load();
    } catch (err) {
      captureError(err, { tags: { entity: "vehicle", operation: "reactivate" } });
      setActionError(err?.message || t("provider.vehicles.reactivateFailed"));
    } finally {
      setReactivatingId(null);
    }
  }

  return (
    <Box>
      {actionError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>{actionError}</Typography>
      )}
      <EntityList
        title={t("provider.vehicles.heading")} rows={visibleRows} loading={visibleRows === null} error={error} canEdit={canEdit}
        entityTag="vehicle"
        searchKeys={VEHICLE_SEARCH_KEYS} sortOptions={vehicleSortOptions(t)}
        headerExtra={
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label={<Typography variant="caption" sx={{ color: "text.secondary" }}>{t("provider.vehicles.showInactiveLabel")}</Typography>}
          />
        }
        deleteConfig={{
          ariaLabel: t("provider.vehicles.deactivateAria"),
          icon: <BlockIcon fontSize="small" />,
          confirmTitle: t("provider.vehicles.deactivateConfirmTitle"),
          confirmBody: t("provider.vehicles.deactivateConfirmBody"),
          confirmActionLabel: t("provider.vehicles.deactivateAria"),
          failLabel: t("provider.vehicles.deactivateFailLabel"),
          color: "warning",
        }}
        canDelete={(v) => v.active}
        renderExtraActions={(v) => (v.active ? null : (
          <IconButton size="small" aria-label={t("provider.vehicles.reactivateAria")} disabled={reactivatingId === v.vehicleId} onClick={() => reactivate(v)}>
            {reactivatingId === v.vehicleId ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon fontSize="small" />}
          </IconButton>
        ))}
        onAdd={() => setEditRow(null)} onEdit={(r) => setEditRow(r)}
        onDelete={async (r) => { await api.setVehicleLastDate({ token, vehicle: r, lastDate: todayISO() }); await load(); }}
        renderRow={(v) => (
          <Box sx={{ opacity: v.active ? 1 : 0.55 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.vehicleNimi || v.vehicleRegNo || t("provider.vehicles.defaultVehicleName")}</Typography>
              {hasGps && (
                v.hasGpsTracking
                  ? <GpsFixedIcon fontSize="small" color="primary" titleAccess={t("provider.vehicles.gpsTrackingLabel")} />
                  : <GpsOffIcon fontSize="small" color="disabled" titleAccess={t("provider.vehicles.noGpsLabel")} />
              )}
              {!v.active && <Chip size="small" label={t("provider.vehicles.deletedChip")} sx={{ height: 18, fontSize: 11 }} />}
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {[v.vehicleRegNo, v.vehicleTypeName, v.vehiclePuomi ? `${v.vehiclePuomi} m` : null].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
        )}
      />
      {editRow !== undefined && (
        <EntityFormDialog open onClose={() => setEditRow(undefined)}
          title={editRow ? t("provider.vehicles.editDialogTitle") : t("provider.vehicles.addDialogTitle")}
          schema={schema} row={editRow} entityTag="vehicle" onSubmit={submit} />
      )}
    </Box>
  );
}
